import { createContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { CartContextProps, CartItem } from '@/types/types';
import AXIOS from '@/util/axios';

export const CartContext = createContext<CartContextProps | undefined>(
  undefined
);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [items, setItems] = useState<CartItem[] | null>(() => {
    const savedCart = localStorage.getItem('cartItems');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [discountCode, setDiscountCode] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { authState } = useAuth();

  // Debugging authState
  useEffect(() => {
    console.log('authState updated:', authState);
  }, [authState]);

  const fetchCart = useCallback(async () => {
    const cartId = authState?.user?.cart?.id;
    console.log('Attempting to fetch cart with cartId:', cartId);

    if (authState?.token && cartId) {
      try {
        const response = await AXIOS.get(`/api/carts/carts/${cartId}`, {
          headers: {
            Authorization: `Bearer ${authState.token}`,
            'Content-Type': 'application/json',
          },
        });
        console.log('Cart fetched successfully:', response.data);

        const fetchedCart = response.data;
        setItems(fetchedCart.items);
        setDiscountCode(fetchedCart.discountCode);

        localStorage.setItem('cartItems', JSON.stringify(fetchedCart.items));
      } catch (error: any) {
        console.error(
          'Error fetching cart:',
          error.response?.data || error.message
        );
      }
    } else {
      console.error('No token or cart ID available');
    }
  }, [authState]);

  useEffect(() => {
    if (authState?.user && authState.token) {
      console.log('Fetching cart because authState is valid');
      fetchCart();
    }
  }, [authState, fetchCart]);

  const updateCart = async (
    cartItems: CartItem[],
    discountCode: string | null
  ) => {
    const cartId = authState?.user?.cart?.id;
    console.log('Updating cart with cartId:', cartId);
    console.log('Cart items:', cartItems);
    console.log('Discount code:', discountCode);

    if (authState?.token && cartId) {
      try {
        const itemsPayload = cartItems.map((item) => ({
          priceRegular: item.priceRegular,
          discountRate: item.discountRate,
          discountAmount: item.discountAmount,
          priceFinal: item.priceFinal,
          course: {
            id: item.course.id,
            name: item.course.name,
            description: item.course.description,
            thumbnailUrl: item.course.thumbnailUrl,
          },
        }));

        const totalPayload = {
          items: itemsPayload,
          discountCode: discountCode ? { id: discountCode } : null,
        };

        console.log('Payload being sent to server:', totalPayload);

        const response = await AXIOS.patch(
          `/api/carts/carts/${cartId}`,
          totalPayload
        );

        console.log('Cart updated successfully:', response.data);

        setItems(response.data.items || []);
        setDiscountCode(response.data.discountCode?.id || null);
        setErrorMessage(null);
      } catch (error: any) {
        console.error(
          'Error updating cart:',
          error.response?.data || error.message
        );
        if (error.response && error.response.status === 422) {
          setErrorMessage('Niepoprawny kod rabatowy.');
        } else if (error.response && error.response.status === 500) {
          setErrorMessage('Server error. Please try again later.');
        } else {
          setErrorMessage('Error updating cart.');
        }
      }
    } else {
      console.error('No cart ID available');
    }
  };

  const addToCart = async (item: CartItem) => {
    console.log('Adding to cart:', item);

    if (!item.course || !item.course.id) {
      console.error('Item course ID is undefined', item);
      return;
    }

    const updatedItems = items ? [...items, item] : [item];
    const cartId = authState?.user?.cart?.id;

    console.log('Updated cart items:', updatedItems);
    localStorage.setItem('cartItems', JSON.stringify(updatedItems));
    console.log(updatedItems);

    if (updatedItems && cartId) {
      try {
        const response = await AXIOS.patch(`/api/carts/carts/${cartId}`, {
          items: updatedItems,
        });
        console.log('Item added to cart successfully:', response.data);
        setItems(response.data.items || []);
        localStorage.setItem(
          'cartItems',
          JSON.stringify(response.data.items || [])
        );
      } catch (error: any) {
        console.error(
          'Error adding item to cart:',
          error.response?.data || error.message
        );
      }
    }
  };

  const removeFromCart = async (itemId: number) => {
    console.log('Removing item with ID from cart:', itemId);

    if (!items) return;

    const updatedItems = items.filter(
      (cartItem) => cartItem.course.id !== itemId
    );

    console.log('Updated cart items after removal:', updatedItems);

    const cartId = authState?.user?.cart?.id;

    if (authState?.token && cartId) {
      try {
        const payload = {
          id: cartId,
          discountCode: null,
          items: updatedItems.map((cartItem) => ({
            course: { id: cartItem.course.id },
          })),
        };

        console.log('Payload being sent to remove item:', payload);

        const response = await AXIOS.patch(
          `/api/carts/carts/${cartId}`,
          payload
        );

        console.log('Item removed from cart successfully:', response.data);

        setItems(response.data.items || []);
        localStorage.setItem(
          'cartItems',
          JSON.stringify(response.data.items || [])
        );
      } catch (error: any) {
        console.error(
          'Error removing item from cart:',
          error.response?.data || error.message
        );
      }
    }
  };

  return (
    <CartContext.Provider
      value={{
        items,
        setItems,
        itemCount: items?.length || 0,
        addToCart,
        removeFromCart,
        applyDiscountCode: async (code) => {
          console.log('Applying discount code:', code);
          await updateCart(items || [], code);
        },
        removeDiscountCode: async () => {
          console.log('Removing discount code');
          await updateCart(items || [], null);
        },
        discountCode,
        errorMessage,
        fetchCart,
        totalRegularPrice:
          items?.reduce(
            (total, item) => total + parseFloat(item.priceRegular),
            0
          ) || 0,
        totalSavings:
          items?.reduce(
            (total, item) => total + parseFloat(item.discountAmount),
            0
          ) || 0,
        totalPrice:
          items?.reduce(
            (total, item) => total + parseFloat(item.priceFinal),
            0
          ) || 0,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
